import { supabase } from "@/integrations/supabase/client";

/**
 * Client helpers for the moderation edge functions. Both throw a friendly
 * Error on block, so callers can let it bubble up to a toast.
 */

/** File a report against a tip or photo pin (Guideline 1.2 — UGC apps must
 *  offer reporting). Callers should also hide the content locally via
 *  utils/contentSafety so the reporter never sees it again. */
export async function reportContent(args: {
  contentType: "tip" | "photo";
  contentId: string;
  authorId?: string | null;
  contentText?: string;
  reason?: string;
}): Promise<void> {
  const { error } = await supabase.functions.invoke("report-content", { body: args });
  if (error) throw new Error(error.message || "Couldn't send the report. Try again.");
}

export async function moderateText(text: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("moderate-text", {
    body: { text },
  });
  if (error) {
    // Network / function-level errors — fail closed.
    throw new Error(error.message || "Couldn't check your message. Try again.");
  }
  if (data && data.allowed === false) {
    throw new Error(data.reason || "Please rephrase — this doesn't fit our community guidelines.");
  }
}

/** `file` should already be the resized JPEG you intend to upload. */
export async function moderateImage(file: Blob, mimeType = "image/jpeg"): Promise<void> {
  const base64 = await blobToBase64(file);
  const { data, error } = await supabase.functions.invoke("moderate-image", {
    body: { imageBase64: base64, mimeType },
  });
  if (error) throw new Error(error.message || "Couldn't check this image. Try again.");
  if (data && data.allowed === false) {
    throw new Error(data.reason || "This image isn't allowed.");
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      // result looks like "data:image/jpeg;base64,XXXX" — strip the prefix.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}
